import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  CircularProgress,
  Divider,
} from "@mui/material";
import { FormattedMessage, useIntl } from "react-intl";
import { Attendee } from "../../../../../../redux/types/types";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../../../../../redux/store";
import { adjustAttendeeClasses } from "../../../../../../redux/admin/adminOperations";
import { getAttendeeSpecialClassAccessApi } from "../../../../../../utils/api/attendees";
import { getLocalizedText } from "../../../../../../utils/localization";
import Notiflix from "notiflix";
import AttendeeImportantInfoButton from "../../../../../Attendees/AttendeeImportantInfoButton";

interface AdjustClassesDialogProps {
  open: boolean;
  onClose: () => void;
  attendee: Attendee;
}

interface ClassAccessInfo {
  classId: number;
  classTitle: Record<string, string>;
  isSpecial: boolean;
  remainingAccess: number;
}

export default function AdjustClassesDialog({
  open,
  onClose,
  attendee,
}: AdjustClassesDialogProps) {
  const dispatch = useDispatch<AppDispatch>();
  const intl = useIntl();
  const [regularClasses, setRegularClasses] = useState<number>(
    attendee.remainingClasses || 0,
  );
  const [specialClasses, setSpecialClasses] = useState<Record<string, number>>(
    {},
  );
  const [reason, setReason] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [classAccessInfo, setClassAccessInfo] = useState<ClassAccessInfo[]>([]);
  const [fetchingClasses, setFetchingClasses] = useState(false);

  useEffect(() => {
    if (open && attendee) {
      setRegularClasses(attendee.remainingClasses || 0);

      // Fetch special class access info
      const fetchClassInfo = async () => {
        setFetchingClasses(true);
        try {
          const response = await getAttendeeSpecialClassAccessApi(attendee.id);
          setClassAccessInfo(response.data);

          // Initialize special classes state
          const initial: Record<string, number> = {};
          response.data.forEach((classInfo: ClassAccessInfo) => {
            initial[classInfo.classId.toString()] = classInfo.remainingAccess;
          });
          setSpecialClasses(initial);
        } catch (error) {
          console.error("Failed to fetch class access info:", error);
        } finally {
          setFetchingClasses(false);
        }
      };

      fetchClassInfo();
    }
  }, [open, attendee]);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await dispatch(
        adjustAttendeeClasses({
          attendeeId: attendee.id,
          adjustmentData: {
            regularClasses,
            specialClasses,
            reason: reason.trim() || undefined,
          },
        }),
      ).unwrap();

      Notiflix.Notify.success(
        intl.formatMessage({
          id: "attendee.adjustClasses.success",
          defaultMessage: "Classes adjusted successfully",
        }),
      );
      onClose();
    } catch (error: any) {
      console.error("Failed to adjust classes:", error);
      Notiflix.Notify.failure(
        intl.formatMessage({
          id: "attendee.adjustClasses.error",
          defaultMessage: "Failed to adjust classes",
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSpecialClassChange = (classId: string, value: string) => {
    const numValue = parseInt(value, 10);
    if (!isNaN(numValue) && numValue >= 0) {
      setSpecialClasses((prev) => ({
        ...prev,
        [classId]: numValue,
      }));
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <FormattedMessage
          id="attendee.adjustClasses.title"
          defaultMessage="Adjust Class Counts"
        />
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
          <Typography variant="subtitle2">
            <FormattedMessage
              id="attendee.adjustClasses.attendeeName"
              defaultMessage="Attendee"
            />
            : {attendee.firstName} {attendee.lastName}
          </Typography>
          <AttendeeImportantInfoButton attendee={attendee} />

          <Divider />

          <TextField
            label={
              <FormattedMessage
                id="attendee.remainingNonSpecialClasses"
                defaultMessage="Regular Classes"
              />
            }
            type="number"
            value={regularClasses}
            onChange={(e) =>
              setRegularClasses(parseInt(e.target.value, 10) || 0)
            }
            InputProps={{ inputProps: { min: 0 } }}
            fullWidth
          />

          {fetchingClasses ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : classAccessInfo.length > 0 ? (
            <>
              <Divider />
              <Typography variant="subtitle2">
                <FormattedMessage
                  id="attendee.adjustClasses.specialClasses"
                  defaultMessage="Special Classes"
                />
              </Typography>
              {classAccessInfo.map((classInfo) => (
                <TextField
                  key={classInfo.classId}
                  label={getLocalizedText(classInfo.classTitle, intl.locale)}
                  type="number"
                  value={specialClasses[classInfo.classId.toString()] || 0}
                  onChange={(e) =>
                    handleSpecialClassChange(
                      classInfo.classId.toString(),
                      e.target.value,
                    )
                  }
                  InputProps={{ inputProps: { min: 0 } }}
                  fullWidth
                />
              ))}
            </>
          ) : null}

          <Divider />

          <TextField
            label={
              <FormattedMessage
                id="attendee.adjustClasses.reason"
                defaultMessage="Reason (optional)"
              />
            }
            multiline
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            fullWidth
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={loading}
        >
          {loading ? (
            <CircularProgress size={24} />
          ) : (
            <FormattedMessage id="common.save" defaultMessage="Save" />
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
